// Task panel (right side on desktop, bottom sheet with tabs on mobile): the history of the tasks
// of the floor (or of the building), filter chips, live task cards, and the way to the board
// (« Vue Kanban », #/taches). Tasks are asked from the agents' discussions (« Lancer comme
// tâche »), the calendar, the notifications and the routines: the panel has no composer.
import {
  h, icon, setText, setAttr, chip, button, emptyState, tabs, badge, iconButton, taskState,
} from '../dom.js';
import { t } from '../i18n/fr.js';
import { relativeTime, formatPercent, deptColor, truncate } from '../format.js';
import { filterTasks, sortTasks, countByFilter, liveProgress, ACTIVE_STATES } from '../store.js';

const LIST_LIMIT = 150;
const PAGE = 100;
// « blocked » is only shown while some task is blocked (or when it is the active filter).
const FILTERS = ['all', 'running', 'waiting', 'done', 'scheduled', 'blocked'];
const MOBILE_QUERY = '(max-width: 767px)';

/** Timestamp shown on a task card for its state (pure). */
export function taskTimeRef(task) {
  switch (task?.state) {
    case 'running': case 'executing': return task.startedAt || task.updatedAt;
    case 'awaiting_approval': return task.waitingSince || task.updatedAt;
    case 'scheduled': return task.dueAt || task.createdAt;
    case 'queued': return task.createdAt;
    default: return task?.finishedAt || task?.updatedAt;
  }
}

/** First non-empty line of a text, cut to `n` chars (pure). */
export function firstLine(text, n = 90) {
  const line = String(text ?? '').split('\n').map((l) => l.trim()).find(Boolean) || '';
  return truncate(line.replace(/^#+\s*/, ''), n);
}

/**
 * Creates the panel.
 * @param {object} ctx app context
 * @param {{onOpenTask: (id: string) => void, onOpenApprovals: () => void, onOpenBoard?: () => void}} handlers
 * @returns {{el: HTMLElement, focusList(): void, destroy(): void}}
 */
export function createTaskPanel(ctx, { onOpenTask, onOpenApprovals, onOpenBoard }) {
  const { store, api, toast } = ctx;
  const offs = [];
  const timers = [];
  const mq = window.matchMedia ? window.matchMedia(MOBILE_QUERY) : { matches: false };

  // ---------------------------------------------------------------------------------------------
  // Task list

  const scopeLabel = h('span', { class: 'tp-scope' });
  const filterDot = h('span', { class: 'chip-dot', 'aria-hidden': 'true' });
  const filterName = h('span', { class: 'chip-label' });
  const deptFilterChip = h('button', {
    type: 'button', class: 'chip chip-filter', hidden: true, title: t('panel.clearFilter'), 'aria-label': t('panel.clearFilter'),
    onClick: () => store.setUi({ deptFilter: null }),
  }, filterDot, filterName, icon('close', { size: 12 }));
  const collapseBtn = iconButton('panelRight', t('panel.collapse'), { class: 'tp-collapse', onClick: () => store.setUi({ panelCollapsed: true }) });
  // The board (Kanban) of the same tasks: columns, drag & drop, retries and ignored problems.
  const boardBtn = button(t('panel.board'), {
    variant: 'secondary', size: 'sm', icon: 'columns', class: 'tp-board', title: t('panel.boardTitle'), onClick: () => onOpenBoard?.(),
  });
  const chips = new Map();
  const filtersEl = h('div', { class: 'tp-filters', role: 'group', 'aria-label': t('filter.label') });
  for (const f of FILTERS) {
    const c = chip(t(`filter.${f}`), { count: 0, tone: f === 'waiting' || f === 'blocked' ? 'amber' : undefined, onClick: () => store.setUi({ taskFilter: f }) });
    chips.set(f, c);
    filtersEl.appendChild(c);
  }
  const list = h('ul', { class: 'task-list', 'aria-label': t('tasks.listLabel') });
  const empty = h('div', { class: 'tp-empty', hidden: true });
  const approvalsBtn = button(t('tasks.openApprovals'), { variant: 'amber', size: 'sm', icon: 'check', class: 'tp-approvals', onClick: () => onOpenApprovals?.() });
  approvalsBtn.hidden = true;
  const loadMoreBtn = button(t('tasks.loadMore'), { variant: 'ghost', size: 'sm', class: 'tp-more' });
  loadMoreBtn.hidden = true;
  const listSection = h('section', { class: 'tp-list', 'aria-labelledby': 'tp-title' },
    h('div', { class: 'tp-list-head' },
      h('h2', { class: 'tp-title', id: 'tp-title', tabIndex: -1 }, t('panel.title')), scopeLabel, deptFilterChip,
      h('span', { class: 'spacer' }), boardBtn, collapseBtn),
    filtersEl, approvalsBtn,
    h('div', { class: 'tp-scroll' }, list, empty, loadMoreBtn));

  const cards = new Map();
  const exhausted = new Set();

  function deptNameOf(id) {
    return store.get().office.departments.find((d) => d.id === id)?.name || '';
  }

  function createCard(tk) {
    const pill = h('span', { class: 'tc-pill' });
    const title = h('span', { class: 'tc-title' });
    const time = h('span', { class: 'tc-time' });
    const meta = h('span', { class: 'tc-meta mono-label' });
    const fill = h('span', { class: 'tc-fill' });
    const bar = h('span', { class: 'tc-bar', role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': '100' }, fill);
    const foot = h('span', { class: 'tc-foot', hidden: true });
    const btn = h('button', { type: 'button', class: 'task-card', dataset: { id: tk.id }, onClick: () => onOpenTask?.(tk.id) },
      h('span', { class: 'tc-row' }, pill, title, time), meta, bar, foot);
    const card = { li: h('li', { class: 'tc-item' }, btn), btn, pill, title, time, meta, bar, fill, foot, task: null, p: -1, footKey: '' };
    updateCard(card, tk);
    return card;
  }

  function updateProgress(card, now = Date.now()) {
    const tk = card.task;
    const p = Math.round(liveProgress(tk, now, store.get().serverOffset || 0));
    setText(card.pill, ACTIVE_STATES.has(tk.state) ? formatPercent(p) : t(`state.${taskState(tk)}`));
    if (p !== card.p) {
      card.p = p;
      card.fill.style.setProperty('transform', `scaleX(${p / 100})`);
      setAttr(card.bar, 'aria-valuenow', String(p));
      setAttr(card.bar, 'aria-label', t('task.progressAria', { p }));
    }
  }

  function updateCard(card, tk, now = Date.now()) {
    card.task = tk;
    card.btn.dataset.state = taskState(tk);
    card.btn.classList.toggle('is-late', !!tk.late);
    const title = tk.title || t('task.untitled');
    setText(card.title, title);
    const agent = tk.agentName || (tk.agentId ? String(tk.agentId).toUpperCase() : t('task.routing'));
    setText(card.meta, t('task.meta', { agent, dept: String(tk.deptName || deptNameOf(tk.deptId)).toUpperCase() }));
    setText(card.time, relativeTime(taskTimeRef(tk), now, { short: true }));
    setAttr(card.btn, 'aria-label', t('task.cardAria', { title, state: t(`state.${taskState(tk)}`) }));
    updateProgress(card, now);
    const attempts = Number(tk.attemptCount) || 0;
    const footKey = `${tk.late ? 1 : 0}|${tk.revisions || 0}|${tk.fileCount || 0}|${attempts}|${tk.state}`;
    if (footKey !== card.footKey) {
      card.footKey = footKey;
      const parts = [];
      if (tk.late) parts.push(badge(t('task.late'), 'amber'));
      // Runs before this one (retries): « 3e essai » while it runs again, « 3 essais » once it ended.
      if (attempts > 0 && !['done', 'failed', 'cancelled', 'rejected'].includes(tk.state)) {
        parts.push(h('span', { class: 'tc-note' }, icon('repeat', { size: 12 }), t('task.attempt', { nth: `${attempts + 1}e` })));
      } else if (attempts > 1) {
        parts.push(h('span', { class: 'tc-note' }, icon('repeat', { size: 12 }), t('task.attempts', { n: attempts })));
      }
      if (tk.revisions > 0) parts.push(h('span', { class: 'tc-note' }, icon('refresh', { size: 12 }), t('task.revisions', { n: tk.revisions })));
      if (tk.fileCount > 0) parts.push(h('span', { class: 'tc-note' }, icon('layers', { size: 12 }), t('task.fileCount', { n: tk.fileCount })));
      card.foot.replaceChildren(...parts);
      card.foot.hidden = !parts.length;
    }
  }

  function renderEmpty(filter, scopedCount) {
    const unavailable = store.get().tasksUnavailable;
    let node;
    if (unavailable) node = emptyState({ icon: 'alert', title: t('tasks.unavailable') });
    else if (!store.get().tasksLoaded) node = emptyState({ icon: 'clock', title: t('tasks.loading') });
    else if (filter === 'waiting') node = emptyState({ icon: 'check', title: t('tasks.emptyWaiting.title'), text: t('tasks.emptyWaiting.text') });
    else if (scopedCount) node = emptyState({ icon: 'search', title: t('tasks.emptyFilter.title'), text: t('tasks.emptyFilter.text') });
    else node = emptyState({ icon: 'inbox', title: t('tasks.empty.title'), text: t('tasks.empty.text') });
    empty.replaceChildren(node);
  }

  function renderList() {
    const s = store.get();
    const { ui } = s;
    const building = ui.view === 'building';
    const scoped = filterTasks(s.tasks.values(), { floorId: ui.floorId, deptId: ui.deptFilter, view: ui.view });
    const counts = countByFilter(scoped);
    for (const [f, c] of chips) {
      const active = ui.taskFilter === f;
      c.classList.toggle('is-active', active);
      setAttr(c, 'aria-pressed', String(active));
      setText(c.querySelector('.chip-count'), counts[f]);
      if (f === 'blocked') c.hidden = !counts.blocked && !active;
    }
    const visible = sortTasks(filterTasks(scoped, { filter: ui.taskFilter, view: 'building' })).slice(0, LIST_LIMIT);
    const seen = new Set();
    let prevNode = null;
    const now = Date.now();
    for (const tk of visible) {
      let card = cards.get(tk.id);
      if (!card) { card = createCard(tk); cards.set(tk.id, card); } else if (card.task !== tk) updateCard(card, tk, now);
      seen.add(tk.id);
      const expected = prevNode ? prevNode.nextSibling : list.firstChild;
      if (card.li !== expected) list.insertBefore(card.li, expected);
      prevNode = card.li;
    }
    for (const [id, card] of cards) if (!seen.has(id)) { card.li.remove(); cards.delete(id); }
    empty.hidden = visible.length > 0;
    if (!visible.length) renderEmpty(ui.taskFilter, scoped.length);
    approvalsBtn.hidden = !(counts.waiting > 1 && (ui.taskFilter === 'waiting' || ui.panel === 'waiting'));
    const scopeKey = building ? '*' : ui.floorId;
    loadMoreBtn.hidden = building || exhausted.has(scopeKey) || scoped.length < 50 || !['all', 'done'].includes(ui.taskFilter);
    // Header texts
    const floor = s.office.floors.find((f) => f.id === ui.floorId);
    setText(scopeLabel, building || !floor ? t('panel.scopeBuilding') : t('panel.scopeFloor'));
    const fd = ui.deptFilter ? s.office.departments.find((d) => d.id === ui.deptFilter) : null;
    deptFilterChip.hidden = !fd;
    if (fd) {
      setText(filterName, fd.name);
      filterDot.style.setProperty('--dot', deptColor(fd.color, fd.id));
    }
    mobileTabs.setCount('waiting', counts.waiting || null);
    railCount.textContent = String(counts.running + counts.waiting);
  }

  loadMoreBtn.addEventListener('click', async () => {
    const s = store.get();
    const floorId = s.ui.floorId;
    const scoped = filterTasks(s.tasks.values(), { floorId, view: 'floor' });
    const oldest = scoped.reduce((min, tk) => Math.min(min, new Date(tk.createdAt || NaN).getTime() || Infinity), Infinity);
    loadMoreBtn.disabled = true;
    try {
      const more = await api.get(api.withQuery('/api/tasks', { floorId, limit: PAGE, before: Number.isFinite(oldest) ? oldest : undefined }));
      const listMore = Array.isArray(more) ? more : [];
      store.setTasks(listMore);
      if (listMore.length < PAGE) exhausted.add(floorId);
      renderList();
    } catch (err) {
      toast.error(err);
    } finally {
      loadMoreBtn.disabled = false;
    }
  });

  // ---------------------------------------------------------------------------------------------
  // Mobile sheet tabs, collapse rail

  const mobileTabs = tabs([
    { id: 'tasks', label: t('panel.tabs.tasks') },
    { id: 'waiting', label: t('panel.tabs.waiting'), count: null },
  ], { active: store.get().ui.panel || 'tasks', label: t('panel.label'), onChange: (id) => setPanelTab(id) });
  const handle = h('button', {
    type: 'button', class: 'tp-handle', 'aria-label': t('panel.sheetToggle'),
    onClick: () => store.setUi({ sheetExpanded: !store.get().ui.sheetExpanded }),
  }, h('span', { class: 'tp-handle-bar', 'aria-hidden': 'true' }));
  const sheetHead = h('div', { class: 'tp-sheet-head' }, handle, mobileTabs);
  const railCount = h('span', { class: 'tp-rail-count' }, '0');
  const rail = h('button', {
    type: 'button', class: 'tp-rail', 'aria-label': t('panel.expand'), title: t('panel.expand'),
    onClick: () => store.setUi({ panelCollapsed: false }),
  }, icon('list', { size: 16 }), h('span', { class: 'tp-rail-label mono-label' }, t('panel.title')), railCount);

  const el = h('aside', { class: 'taskpanel', id: 'tasks', 'aria-label': t('panel.label') },
    sheetHead, h('div', { class: 'tp-body' }, listSection), rail);

  function setPanelTab(id) {
    const patch = { panel: id === 'waiting' ? 'waiting' : 'tasks' };
    if (id === 'waiting') patch.taskFilter = 'waiting';
    else if (id === 'tasks' && store.get().ui.taskFilter === 'waiting' && store.get().ui.panel === 'waiting') patch.taskFilter = 'all';
    store.setUi(patch);
  }

  function syncPanelUi() {
    const { ui } = store.get();
    const tab = ui.panel === 'waiting' ? 'waiting' : 'tasks';
    el.dataset.tab = tab;
    mobileTabs.setActive(tab);
    el.classList.toggle('is-collapsed', !!ui.panelCollapsed);
    el.classList.toggle('is-expanded', !!ui.sheetExpanded);
  }

  // ---------------------------------------------------------------------------------------------
  // Wiring

  offs.push(store.on('office', renderList));
  offs.push(store.on('tasks', renderList));
  offs.push(store.on('tasksLoaded', renderList));
  offs.push(store.on('tasksUnavailable', renderList));
  offs.push(store.on('ui', (ui, state, prev) => {
    const p = prev?.ui || {};
    const scope = `${ui.view}|${ui.floorId}|${ui.deptFilter}`;
    if (scope !== `${p.view}|${p.floorId}|${p.deptFilter}` || ui.taskFilter !== p.taskFilter || ui.panel !== p.panel) renderList();
    if (ui.panel !== p.panel || ui.panelCollapsed !== p.panelCollapsed || ui.sheetExpanded !== p.sheetExpanded) syncPanelUi();
  }));

  // Live progress every second; relative times every 20 s.
  let ticks = 0;
  timers.push(setInterval(() => {
    if (document.hidden) return;
    ticks += 1;
    const now = Date.now();
    for (const card of cards.values()) {
      if (ticks % 20 === 0) updateCard(card, card.task, now);
      else if (ACTIVE_STATES.has(card.task.state)) updateProgress(card, now);
    }
  }, 1000));

  syncPanelUi();
  renderList();

  return {
    el,
    /** Moves the focus to the task list (the skip link): expands a collapsed panel, the sheet on a phone. */
    focusList() {
      const patch = { panelCollapsed: false };
      if (mq.matches) patch.sheetExpanded = true;
      store.setUi(patch);
      requestAnimationFrame(() => listSection.querySelector('.tp-title')?.focus());
    },
    destroy() {
      for (const off of offs) off();
      for (const id of timers) clearInterval(id);
    },
  };
}
